'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { reportesService, PipelineData, PipelineSolicitudItem } from '@/lib/api/reportesService';
import { solicitudService } from '@/lib/api/solicitudService';
import { useAuth } from '@/hooks/useAuth';
import type { Solicitud } from '@/types/solicitud';
import { Button } from '@/components/ui/Button';
import ErrorMessage from '@/components/common/ErrorMessage';
import { Input } from '@/components/ui/Input';

const statusOrder = [
  'CONTACTO',
  'ENTREGA A RIESGOS',
  'AUTORIZADO',
  'CONTRATO IMPRESO',
  'FIRMAR CONTRATO',
] as const;

const statusLabels: Record<string, string> = {
  'CONTACTO': 'CONTACTO',
  'ENTREGA A RIESGOS': 'ENTREGA A RIESGOS',
  'AUTORIZADO': 'AUTORIZADO',
  'CONTRATO IMPRESO': 'CONTRATO IMPRESO',
  'FIRMAR CONTRATO': 'FIRMAR CONTRATO',
  'ENPROCESO': 'EN PROCESO',
  'FONDEADO': 'FONDEADO',
  'RECHAZADO': 'RECHAZADO',
  'TOTAL': 'TOTAL',
};

const PipelineManagement: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [data, setData] = useState<PipelineData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [fecha, setFecha] = useState('');
  const [producto, setProducto] = useState('');
  const [frecuencia, setFrecuencia] = useState('1');
  const printRef = useRef<HTMLDivElement>(null);
  const [visorSolicitudesVisible, setVisorSolicitudesVisible] = useState(false);
  const [listadoSolicitudes, setListadoSolicitudes] = useState<PipelineSolicitudItem[]>([]);
  const [solicitudDetalle, setSolicitudDetalle] = useState<Solicitud | null>(null);
  const [selectedCellLabel, setSelectedCellLabel] = useState('');
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  type ColumnaPipeline = typeof statusOrder[number] | 'ENPROCESO' | 'FONDEADO' | 'RECHAZADO' | 'TOTAL';
  const toggleVisorSolicitudes = useCallback(() => {
    setVisorSolicitudesVisible((v) => !v);
    if (!visorSolicitudesVisible) {
      setListadoSolicitudes([]);
      setSolicitudDetalle(null);
      setSelectedCellLabel('');
    }
  }, [visorSolicitudesVisible]);

  const mostrarDetallePipeline = useCallback(
    (fechaStr: string, productoNombre: string, asesorNombre: string, columna: ColumnaPipeline) => {
      const row = data.find(
        (r) => r.producto === productoNombre && r.fecha === fechaStr && r.asesor === asesorNombre
      );
      if (!row?.solicitudes) {
        setListadoSolicitudes([]);
        setSelectedCellLabel('');
        return;
      }
      const ids = row[columna]?.ids;
      if (!ids?.length) {
        setListadoSolicitudes([]);
        setSelectedCellLabel(`${productoNombre} · ${fechaStr} · ${asesorNombre} · ${statusLabels[columna] ?? columna}`);
        return;
      }
      setSelectedCellLabel(`${productoNombre} · ${fechaStr} · ${asesorNombre} · ${statusLabels[columna] ?? columna}`);
      const filtered = row.solicitudes.filter((s) => ids.includes(s.id));
      setListadoSolicitudes(filtered);
      setSolicitudDetalle(null);
    },
    [data]
  );

  const mostrarSolicitudDetalle = useCallback(async (id: string) => {
    setLoadingDetalle(true);
    setSolicitudDetalle(null);
    try {
      const res = await solicitudService.getById(id);
      if (res?.data) setSolicitudDetalle(res.data as Solicitud);
    } catch {
      setSolicitudDetalle(null);
    } finally {
      setLoadingDetalle(false);
    }
  }, []);

  const formatCurrencyShort = useCallback((value: number) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(value);
  }, []);

  const handlePrint = () => {
    const elem = printRef.current;
    if (!elem) return;
    const viewName = 'PipeLine';
    const title = viewName === 'PipeLine' ? 'SOLICITUDES EN PROCESO' : viewName;
    const now = new Date();
    const dateStr = now.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
    let tablaEncabezado = '<table style="width:100%;border-collapse:collapse;margin-bottom:16px"><tr>';
    tablaEncabezado += `<td style="padding:8px;font-size:18px;font-weight:bold">${title}</td>`;
    tablaEncabezado += `<td style="padding:8px;text-align:right">${dateStr}</td>`;
    tablaEncabezado += '</tr></table>';
    const printStyles = `
      body { font-family: system-ui, sans-serif; font-size: 9px; padding: 8px; }
      table { border-collapse: collapse; width: 100%; }
      th, td { border: 1px solid #ccc; padding: 3px 5px; text-align: left; }
      thead th { background: #f3f4f6 !important; font-weight: 600;
                 -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      tr { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .bg-yellow-50, .bg-yellow-100 { background: #fef9c3 !important; }
      .bg-yellow-200 { background: #fef08a !important; }
      .bg-green-50, .bg-green-100 { background: #dcfce7 !important; }
      .bg-green-200 { background: #bbf7d0 !important; }
      .bg-red-50, .bg-red-100 { background: #fee2e2 !important; }
      .bg-red-200 { background: #fecaca !important; }
      .bg-black { background: #000 !important; color: #fff !important; }
      .bg-gray-100 { background: #f3f4f6 !important; }
      .bg-gray-200 { background: #e5e7eb !important; }
      .text-white { color: #fff !important; }
      @page { size: landscape; margin: 0.5cm; }
    `;
    const contenido =
      '<html><head><title>' +
      viewName +
      '</title><style>' +
      printStyles +
      '</style></head><body><header>' +
      tablaEncabezado +
      '</header>' +
      DOMPurify.sanitize(elem.innerHTML) +
      '</body></html>';
    const mywindow = window.open('', 'PRINT');
    if (!mywindow) return;
    mywindow.document.write(contenido);
    mywindow.document.close();
    mywindow.focus();
    mywindow.print();
    mywindow.close();
  };

  const load = async () => {
    setIsLoading(true);
    setError(null);
    const res = await reportesService.getPipeline({
      fechaInicio: fechaInicio || undefined,
      fechaFin: fechaFin || undefined,
      fecha: fecha || undefined,
      producto: producto || undefined,
      frecuencia: frecuencia || '1',
    });
    if (res.data) setData(res.data);
    else setError(res.error || 'Error al cargar reporte de Pipeline');
    setIsLoading(false);
  };

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      load();
    }
  }, [authLoading, isAuthenticated]);

  const productos = useMemo(() => {
    const set = new Set<string>();
    data.forEach((p) => set.add(p.producto));
    return Array.from(set).sort();
  }, [data]);

  // Agrupar datos por producto, fecha y asesor
  const groupedData = useMemo(() => {
    const grouped: Record<string, Record<string, Record<string, PipelineData>>> = {};
    
    data.forEach((row) => {
      if (!grouped[row.producto]) {
        grouped[row.producto] = {};
      }
      if (!grouped[row.producto][row.fecha]) {
        grouped[row.producto][row.fecha] = {};
      }
      grouped[row.producto][row.fecha][row.asesor] = row;
    });
    
    return grouped;
  }, [data]);

  // Calcular totales por producto y fecha
  const calculateTotals = (rows: PipelineData[]) => {
    const totals: Record<string, { numero: number; monto: number }> = {
      'CONTACTO': { numero: 0, monto: 0 },
      'ENTREGA A RIESGOS': { numero: 0, monto: 0 },
      'AUTORIZADO': { numero: 0, monto: 0 },
      'CONTRATO IMPRESO': { numero: 0, monto: 0 },
      'FIRMAR CONTRATO': { numero: 0, monto: 0 },
      'ENPROCESO': { numero: 0, monto: 0 },
      'FONDEADO': { numero: 0, monto: 0 },
      'RECHAZADO': { numero: 0, monto: 0 },
      'TOTAL': { numero: 0, monto: 0 },
    };
    
    rows.forEach((row) => {
      totals['CONTACTO'].numero += row.CONTACTO.numero;
      totals['CONTACTO'].monto += row.CONTACTO.monto;
      totals['ENTREGA A RIESGOS'].numero += row['ENTREGA A RIESGOS'].numero;
      totals['ENTREGA A RIESGOS'].monto += row['ENTREGA A RIESGOS'].monto;
      totals['AUTORIZADO'].numero += row.AUTORIZADO.numero;
      totals['AUTORIZADO'].monto += row.AUTORIZADO.monto;
      totals['CONTRATO IMPRESO'].numero += row['CONTRATO IMPRESO'].numero;
      totals['CONTRATO IMPRESO'].monto += row['CONTRATO IMPRESO'].monto;
      totals['FIRMAR CONTRATO'].numero += row['FIRMAR CONTRATO'].numero;
      totals['FIRMAR CONTRATO'].monto += row['FIRMAR CONTRATO'].monto;
      totals['ENPROCESO'].numero += row.ENPROCESO.numero;
      totals['ENPROCESO'].monto += row.ENPROCESO.monto;
      totals['FONDEADO'].numero += row.FONDEADO.numero;
      totals['FONDEADO'].monto += row.FONDEADO.monto;
      totals['RECHAZADO'].numero += row.RECHAZADO.numero;
      totals['RECHAZADO'].monto += row.RECHAZADO.monto;
      totals['TOTAL'].numero += row.TOTAL.numero;
      totals['TOTAL'].monto += row.TOTAL.monto;
    });
    
    return totals;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">PipeLine</h1>
          <p className="text-sm text-gray-500 mt-1">
            Vista consolidada del embudo de ventas con métricas por etapa, producto, asesor y fecha.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={load} disabled={isLoading}>
            Consultar
          </Button>
          <Button variant="secondary" onClick={handlePrint} disabled={data.length === 0}>
            Imprimir
          </Button>
          <Button variant="secondary" onClick={toggleVisorSolicitudes}>
            {visorSolicitudesVisible ? 'Ocultar Solicitudes' : 'Ver Solicitudes'}
          </Button>
        </div>
      </div>

      {error && <ErrorMessage message={error} />}

      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 items-end">
          <Input 
            label="Fecha inicio" 
            type="date" 
            value={fechaInicio} 
            onChange={(e) => setFechaInicio(e.target.value)}
            min="2019-01-02"
          />
          <Input 
            label="Fecha fin" 
            type="date" 
            value={fechaFin} 
            onChange={(e) => setFechaFin(e.target.value)}
            min="2019-01-02"
          />
          <Input 
            label="Fecha referencia" 
            type="date" 
            value={fecha} 
            onChange={(e) => setFecha(e.target.value)}
            min="2019-01-02"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Producto</label>
            <select
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={producto}
              onChange={(e) => setProducto(e.target.value)}
            >
              <option value="">Todos</option>
              {productos.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Frecuencia</label>
            <select
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={frecuencia}
              onChange={(e) => setFrecuencia(e.target.value)}
            >
              <option value="1">Fin de Mes</option>
              <option value="2">Diario</option>
              <option value="3">Mismo día hábil</option>
            </select>
          </div>
        </div>
      </div>

      <div
        ref={printRef}
        id="listadoPipeLine"
        className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto"
        style={visorSolicitudesVisible ? { maxHeight: 'calc(100vh - 380px)', overflow: 'auto' } : undefined}
      >
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-700 sticky top-0">
            <tr>
              <th className="text-left p-3 border-r border-gray-200">PRODUCTO</th>
              <th className="text-left p-3 border-r border-gray-200">FECHA</th>
              <th className="text-left p-3 border-r border-gray-200">ASESOR</th>
              {statusOrder.map((s) => (
                <th key={s} className="text-left p-3 border-r border-gray-200">{statusLabels[s]}</th>
              ))}
              <th className="text-left p-3 border-r border-gray-200 bg-yellow-100">EN PROCESO</th>
              <th className="text-left p-3 border-r border-gray-200 bg-green-100">FONDEADO</th>
              <th className="text-left p-3 border-r border-gray-200 bg-red-100">RECHAZADO</th>
              <th className="text-left p-3 bg-black text-white">TOTAL</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {Object.entries(groupedData).map(([productoNombre, fechas]) => {
              const productoRows: PipelineData[] = [];
              Object.values(fechas).forEach((asesores) => {
                Object.values(asesores).forEach((row) => productoRows.push(row));
              });
              const productoTotals = calculateTotals(productoRows);
              
              return (
                <React.Fragment key={productoNombre}>
                  {Object.entries(fechas).map(([fechaStr, asesores]) => {
                    const fechaRows = Object.values(asesores);
                    const fechaTotals = calculateTotals(fechaRows);
                    
                    return (
                      <React.Fragment key={`${productoNombre}-${fechaStr}`}>
                        {Object.entries(asesores).map(([asesorNombre, row], idx) => (
                          <tr 
                            key={`${productoNombre}-${fechaStr}-${asesorNombre}`}
                            className={`hover:bg-blue-50/40 transition-colors ${
                              idx === 0 ? 'border-t-2 border-gray-300' : ''
                            }`}
                          >
                            {idx === 0 && (
                              <>
                                <td 
                                  rowSpan={Object.keys(asesores).length} 
                                  className="p-3 font-medium border-r border-gray-200 align-top"
                                >
                                  {productoNombre}
                                </td>
                                <td 
                                  rowSpan={Object.keys(asesores).length} 
                                  className="p-3 border-r border-gray-200 align-top"
                                >
                                  {new Date(fechaStr).toLocaleDateString('es-MX')}
                                </td>
                              </>
                            )}
                            <td className="p-3 border-r border-gray-200">{asesorNombre}</td>
                            {statusOrder.map((s) => (
                              <td
                                key={s}
                                className="p-3 border-r border-gray-200 cursor-pointer hover:bg-blue-100"
                                onClick={() => mostrarDetallePipeline(fechaStr, productoNombre, asesorNombre, s)}
                                title="Ver solicitudes"
                              >
                                <div className="text-xs">
                                  <div className="font-semibold">{row[s].numero}</div>
                                  <div className="text-gray-600">
                                    {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(row[s].monto)}
                                  </div>
                                </div>
                              </td>
                            ))}
                            <td
                              className="p-3 border-r border-gray-200 bg-yellow-50 cursor-pointer hover:bg-yellow-100"
                              onClick={() => mostrarDetallePipeline(fechaStr, productoNombre, asesorNombre, 'ENPROCESO')}
                              title="Ver solicitudes"
                            >
                              <div className="text-xs">
                                <div className="font-semibold">{row.ENPROCESO.numero}</div>
                                <div className="text-gray-600">
                                  {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(row.ENPROCESO.monto)}
                                </div>
                              </div>
                            </td>
                            <td
                              className="p-3 border-r border-gray-200 bg-green-50 cursor-pointer hover:bg-green-100"
                              onClick={() => mostrarDetallePipeline(fechaStr, productoNombre, asesorNombre, 'FONDEADO')}
                              title="Ver solicitudes"
                            >
                              <div className="text-xs">
                                <div className="font-semibold">{row.FONDEADO.numero}</div>
                                <div className="text-gray-600">
                                  {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(row.FONDEADO.monto)}
                                </div>
                              </div>
                            </td>
                            <td
                              className="p-3 border-r border-gray-200 bg-red-50 cursor-pointer hover:bg-red-100"
                              onClick={() => mostrarDetallePipeline(fechaStr, productoNombre, asesorNombre, 'RECHAZADO')}
                              title="Ver solicitudes"
                            >
                              <div className="text-xs">
                                <div className="font-semibold">{row.RECHAZADO.numero}</div>
                                <div className="text-gray-600">
                                  {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(row.RECHAZADO.monto)}
                                </div>
                              </div>
                            </td>
                            <td
                              className="p-3 bg-black text-white cursor-pointer hover:opacity-90"
                              onClick={() => mostrarDetallePipeline(fechaStr, productoNombre, asesorNombre, 'TOTAL')}
                              title="Ver solicitudes"
                            >
                              <div className="text-xs">
                                <div className="font-semibold">{row.TOTAL.numero}</div>
                                <div>
                                  {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(row.TOTAL.monto)}
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {/* Fila de totales por fecha */}
                        <tr className="bg-gray-100 font-semibold">
                          <td colSpan={3} className="p-3 border-r border-gray-200 text-right">
                            Total {fechaStr}
                          </td>
                          {statusOrder.map((s) => (
                            <td key={s} className="p-3 border-r border-gray-200">
                              <div className="text-xs">
                                <div>{fechaTotals[s]?.numero || 0}</div>
                                <div className="text-gray-600">
                                  {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(fechaTotals[s]?.monto || 0)}
                                </div>
                              </div>
                            </td>
                          ))}
                          <td className="p-3 border-r border-gray-200 bg-yellow-100">
                            <div className="text-xs">
                              <div>{fechaTotals.ENPROCESO.numero}</div>
                              <div>{new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(fechaTotals.ENPROCESO.monto)}</div>
                            </div>
                          </td>
                          <td className="p-3 border-r border-gray-200 bg-green-100">
                            <div className="text-xs">
                              <div>{fechaTotals.FONDEADO.numero}</div>
                              <div>{new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(fechaTotals.FONDEADO.monto)}</div>
                            </div>
                          </td>
                          <td className="p-3 border-r border-gray-200 bg-red-100">
                            <div className="text-xs">
                              <div>{fechaTotals.RECHAZADO.numero}</div>
                              <div>{new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(fechaTotals.RECHAZADO.monto)}</div>
                            </div>
                          </td>
                          <td className="p-3 bg-black text-white">
                            <div className="text-xs">
                              <div>{fechaTotals.TOTAL.numero}</div>
                              <div>{new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(fechaTotals.TOTAL.monto)}</div>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                  {/* Fila de totales por producto */}
                  <tr className="bg-gray-200 font-bold">
                    <td colSpan={3} className="p-3 border-r border-gray-300 text-right">
                      Total {productoNombre}
                    </td>
                    {statusOrder.map((s) => (
                      <td key={s} className="p-3 border-r border-gray-300">
                        <div className="text-xs">
                          <div>{productoTotals[s]?.numero || 0}</div>
                          <div className="text-gray-700">
                            {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(productoTotals[s]?.monto || 0)}
                          </div>
                        </div>
                      </td>
                    ))}
                    <td className="p-3 border-r border-gray-300 bg-yellow-200">
                      <div className="text-xs">
                        <div>{productoTotals.ENPROCESO.numero}</div>
                        <div>{new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(productoTotals.ENPROCESO.monto)}</div>
                      </div>
                    </td>
                    <td className="p-3 border-r border-gray-300 bg-green-200">
                      <div className="text-xs">
                        <div>{productoTotals.FONDEADO.numero}</div>
                        <div>{new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(productoTotals.FONDEADO.monto)}</div>
                      </div>
                    </td>
                    <td className="p-3 border-r border-gray-300 bg-red-200">
                      <div className="text-xs">
                        <div>{productoTotals.RECHAZADO.numero}</div>
                        <div>{new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(productoTotals.RECHAZADO.monto)}</div>
                      </div>
                    </td>
                    <td className="p-3 bg-black text-white">
                      <div className="text-xs">
                        <div>{productoTotals.TOTAL.numero}</div>
                        <div>{new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(productoTotals.TOTAL.monto)}</div>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
            {data.length === 0 && (
              <tr>
                <td className="p-6 text-gray-500 text-center" colSpan={12}>
                  Sin registros con los filtros actuales.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {visorSolicitudesVisible && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" style={{ minHeight: 300 }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-gray-200">
            <div className="lg:col-span-2 border-r border-gray-200 flex flex-col min-h-0">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-700">
                Solicitudes {selectedCellLabel && ` · ${selectedCellLabel}`}
              </div>
              <div className="overflow-auto flex-1 min-h-0">
                {listadoSolicitudes.length === 0 ? (
                  <p className="p-4 text-sm text-gray-500">Haga clic en una celda del reporte (Contacto, Entregados, Autorizados, etc.) para ver las solicitudes.</p>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left font-medium text-gray-600">Ver</th>
                        <th className="px-2 py-2 text-left font-medium text-gray-600">No. Control</th>
                        <th className="px-2 py-2 text-left font-medium text-gray-600">Cliente</th>
                        <th className="px-2 py-2 text-left font-medium text-gray-600">Producto</th>
                        <th className="px-2 py-2 text-right font-medium text-gray-600">Monto</th>
                        <th className="px-2 py-2 text-left font-medium text-gray-600">Estatus</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {listadoSolicitudes.map((s) => (
                        <tr key={s.id} className="hover:bg-blue-50/40">
                          <td className="px-2 py-2">
                            <button
                              type="button"
                              className="text-blue-600 underline hover:no-underline"
                              onClick={() => mostrarSolicitudDetalle(s.id)}
                            >
                              Ver
                            </button>
                          </td>
                          <td className="px-2 py-2">{s.solicitudNumeroControl ?? '—'}</td>
                          <td className="px-2 py-2">
                            {[s.clienteNombre, s.clienteApellidoPaterno, s.clienteApellidoMaterno].filter(Boolean).join(' ') || s.clienteEmpresa || '—'}
                          </td>
                          <td className="px-2 py-2">{s.producto ?? '—'}</td>
                          <td className="px-2 py-2 text-right">
                            {(s.montoAutorizado ?? s.montoSolicitado) != null ? formatCurrencyShort((s.montoAutorizado ?? s.montoSolicitado)!) : '—'}
                          </td>
                          <td className="px-2 py-2">{s.solicitudEstatus ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            <div className="flex flex-col min-h-0">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-700">
                Detalle solicitud
              </div>
              <div className="overflow-auto flex-1 p-3 text-sm">
                {loadingDetalle && <p className="text-gray-500">Cargando…</p>}
                {!loadingDetalle && !solicitudDetalle && <p className="text-gray-500">Seleccione &quot;Ver&quot; en una fila para ver el detalle.</p>}
                {!loadingDetalle && solicitudDetalle && (
                  <dl className="space-y-1.5">
                    {[
                      ['No. Control', solicitudDetalle.solicitudNumeroControl],
                      ['Cliente', [solicitudDetalle.clienteNombre, solicitudDetalle.clienteApellidoPaterno, solicitudDetalle.clienteApellidoMaterno].filter(Boolean).join(' ') || solicitudDetalle.clienteEmpresa],
                      ['Empresa', solicitudDetalle.clienteEmpresa],
                      ['Producto', solicitudDetalle.producto],
                      ['Estatus', solicitudDetalle.solicitudEstatus],
                      ['Monto solicitado', solicitudDetalle.montoSolicitado != null ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(solicitudDetalle.montoSolicitado) : ''],
                      ['Monto autorizado', solicitudDetalle.montoAutorizado != null ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(solicitudDetalle.montoAutorizado) : ''],
                      ['Fecha contacto', solicitudDetalle.fechaContacto],
                      ['Fecha entrega riesgos', solicitudDetalle.fechaEntregaARiesgos],
                      ['Fecha contrato impreso', solicitudDetalle.fechaContratoImpreso],
                      ['Asesor', solicitudDetalle.asesorNombre],
                    ].map(([label, value]) => (value != null && value !== '' ? (
                      <div key={String(label)}>
                        <dt className="text-gray-500 font-medium">{label}</dt>
                        <dd className="text-gray-900">{String(value)}</dd>
                      </div>
                    ) : null))}
                  </dl>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineManagement;
